<template>
  <div class="space-y-6">
    <!-- Заголовок и кнопка создания -->
    <div class="flex justify-between items-center">
      <h1 class="text-3xl font-bold">Управление услугами</h1>
      <button
        @click="showCreateForm = !showCreateForm"
        class="bg-blue-600 text-white px-6 py-2 rounded font-bold hover:bg-blue-700"
      >
        {{ showCreateForm ? '✕ Отмена' : '+ Новая услуга' }}
      </button>
    </div>

    <!-- Форма создания/редактирования -->
    <div v-if="showCreateForm" class="bg-white p-6 rounded-lg shadow">
      <h2 class="text-xl font-bold mb-4">
        {{ editingService ? 'Редактировать услугу' : 'Создать новую услугу' }}
      </h2>

      <form @submit.prevent="saveService" class="space-y-4">
        <!-- Ошибка -->
        <div v-if="formError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {{ formError }}
        </div>

        <div class="grid grid-cols-2 gap-4">
          <!-- Название -->
          <div>
            <label class="block text-sm font-bold mb-2">Название</label>
            <input
              v-model="serviceForm.name"
              type="text"
              required
              class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
              placeholder="Замена экрана"
            />
          </div>

          <!-- Категория -->
          <div>
            <label class="block text-sm font-bold mb-2">Категория</label>
            <select
              v-model="serviceForm.category"
              required
              class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            >
              <option value="">Выберите категорию</option>
              <option value="screen">Замена экрана</option>
              <option value="battery">Замена батареи</option>
              <option value="motherboard">Ремонт материнской платы</option>
              <option value="water-damage">Восстановление после воды</option>
              <option value="software">Ремонт ПО</option>
              <option value="other">Другое</option>
            </select>
          </div>

          <!-- Цена -->
          <div>
            <label class="block text-sm font-bold mb-2">Цена (₴)</label>
            <input
              v-model.number="serviceForm.price"
              type="number"
              min="0"
              required
              class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
              placeholder="500"
            />
          </div>

          <!-- Время выполнения -->
          <div>
            <label class="block text-sm font-bold mb-2">Время выполнения (часов)</label>
            <input
              v-model.number="serviceForm.estimatedTime"
              type="number"
              min="0"
              required
              class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
              placeholder="1"
            />
          </div>
        </div>

        <!-- Описание -->
        <div>
          <label class="block text-sm font-bold mb-2">Описание</label>
          <textarea
            v-model="serviceForm.description"
            required
            class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            placeholder="Подробное описание услуги"
            rows="3"
          ></textarea>
        </div>

        <!-- Совместимость -->
        <div>
          <label class="block text-sm font-bold mb-2">Совместимость (через запятую)</label>
          <input
            v-model="compatibilityText"
            type="text"
            class="w-full border px-3 py-2 rounded focus:outline-none focus:border-blue-500"
            placeholder="iPhone, Samsung, Android, Xiaomi"
          />
        </div>

        <!-- Кнопки -->
        <div class="flex gap-2">
          <button
            type="submit"
            :disabled="formLoading"
            class="flex-1 bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
          >
            {{ formLoading ? 'Сохранение...' : (editingService ? 'Обновить' : 'Создать') }}
          </button>
          <button
            type="button"
            @click="resetForm"
            class="flex-1 bg-gray-600 text-white py-2 rounded font-bold hover:bg-gray-700"
          >
            Отмена
          </button>
        </div>
      </form>
    </div>

    <!-- Таблица услуг -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-6 border-b">
        <h2 class="text-xl font-bold">Услуги ({{ services.length }})</h2>
      </div>

      <div v-if="loading" class="p-6 text-center text-gray-600">
        Загрузка услуг...
      </div>

      <div v-else-if="services.length === 0" class="p-6 text-center text-gray-600">
        Нет услуг. Создайте первую!
      </div>

      <table v-else class="w-full">
        <thead class="bg-gray-50 border-b">
          <tr>
            <th class="px-6 py-3 text-left text-sm font-bold">Название</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Категория</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Цена</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Время</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Статус</th>
            <th class="px-6 py-3 text-left text-sm font-bold">Действия</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          <tr v-for="service in services" :key="service._id" class="hover:bg-gray-50">
            <td class="px-6 py-4 font-bold">{{ service.name }}</td>
            <td class="px-6 py-4 text-sm">
              <span class="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs">
                {{ service.category }}
              </span>
            </td>
            <td class="px-6 py-4 font-bold">₴{{ service.price }}</td>
            <td class="px-6 py-4 text-sm">~{{ service.estimatedTime }}ч.</td>
            <td class="px-6 py-4">
              <span
                :class="service.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                class="px-3 py-1 rounded-full text-xs font-bold"
              >
                {{ service.isActive ? 'Активна' : 'Неактивна' }}
              </span>
            </td>
            <td class="px-6 py-4 space-x-2">
              <button
                @click="editService(service)"
                class="bg-blue-100 text-blue-600 px-3 py-1 rounded text-sm hover:bg-blue-200"
              >
                Редактировать
              </button>
              <button
                @click="toggleService(service._id, service.isActive)"
                :class="service.isActive ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'"
                class="px-3 py-1 rounded text-sm hover:opacity-80"
              >
                {{ service.isActive ? 'Отключить' : 'Включить' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../../stores/auth';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

export default {
  name: 'AdminServices',
  setup() {
    const authStore = useAuthStore();
    const services = ref([]);
    const loading = ref(false);
    const formLoading = ref(false);
    const showCreateForm = ref(false);
    const formError = ref(null);
    const editingService = ref(null);
    const compatibilityText = ref('');

    const serviceForm = ref({
      name: '',
      description: '',
      category: '',
      price: null,
      estimatedTime: null,
      compatibility: []
    });

    const loadServices = async () => {
      loading.value = true;
      try {
        const response = await axios.get(`${API_URL}/services`);
        services.value = response.data;
      } catch (error) {
        console.error('Ошибка при загрузке услуг:', error);
      } finally {
        loading.value = false;
      }
    };

    const editService = (service) => {
      editingService.value = service;
      serviceForm.value = { ...service };
      compatibilityText.value = service.compatibility?.join(', ') || '';
      showCreateForm.value = true;
    };

    const resetForm = () => {
      serviceForm.value = {
        name: '',
        description: '',
        category: '',
        price: null,
        estimatedTime: null,
        compatibility: []
      };
      compatibilityText.value = '';
      editingService.value = null;
      showCreateForm.value = false;
      formError.value = null;
    };

    const saveService = async () => {
      formError.value = null;

      if (!serviceForm.value.name || !serviceForm.value.category || !serviceForm.value.price) {
        formError.value = 'Заполните все обязательные поля';
        return;
      }

      // Разбираем совместимость
      serviceForm.value.compatibility = compatibilityText.value
        .split(',')
        .map(s => s.trim())
        .filter(s => s);

      formLoading.value = true;

      try {
        if (editingService.value) {
          // Обновление
          await axios.put(
            `${API_URL}/services/${editingService.value._id}`,
            serviceForm.value,
            {
              headers: {
                Authorization: `Bearer ${authStore.token}`
              }
            }
          );
          alert('Услуга обновлена');
        } else {
          // Создание
          await axios.post(
            `${API_URL}/services`,
            serviceForm.value,
            {
              headers: {
                Authorization: `Bearer ${authStore.token}`
              }
            }
          );
          alert('Услуга создана');
        }

        await loadServices();
        resetForm();
      } catch (error) {
        formError.value = error.response?.data?.error || 'Ошибка при сохранении';
      } finally {
        formLoading.value = false;
      }
    };

    const toggleService = async (serviceId, currentStatus) => {
      try {
        await axios.put(
          `${API_URL}/services/${serviceId}`,
          { isActive: !currentStatus },
          {
            headers: {
              Authorization: `Bearer ${authStore.token}`
            }
          }
        );
        await loadServices();
      } catch (error) {
        alert('Ошибка при изменении статуса услуги');
      }
    };

    onMounted(() => {
      loadServices();
    });

    return {
      services,
      loading,
      formLoading,
      showCreateForm,
      formError,
      editingService,
      serviceForm,
      compatibilityText,
      loadServices,
      editService,
      resetForm,
      saveService,
      toggleService
    };
  }
};
</script>
